import { Hole, Rectangle, CalibrationConfig } from '../types';

/**
 * Convert drawing annotation to calibration configuration
 * 
 * Analyzes the drawn annotation image and extracts zone information
 * to create a working calibration config.
 */

interface ColorZone {
  color: string;
  label: string;
  points: Array<{ x: number; y: number }>;
}

const WALL_COLORS = {
  'start': '#00BCD4',  // Cyan
  '10pt': '#8BC34A',   // Light Green
  '20pt': '#CDDC39',   // Lime
  '30pt': '#4CAF50',   // Green
  '40pt': '#2196F3',   // Blue
  '50pt': '#FFC107',   // Yellow
  '100pt': '#f44336',  // Red
  'gutter': '#9C27B0', // Purple
  'custom': '#FF9800'  // Orange
} as const;

/**
 * Parse annotation canvas and extract calibration data
 */
export function parseAnnotationToCalibration(
  annotationCanvas: HTMLCanvasElement,
  videoWidth: number,
  videoHeight: number
): CalibrationConfig {
  const ctx = annotationCanvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) {
    throw new Error('Failed to get canvas context');
  }

  const imageData = ctx.getImageData(0, 0, annotationCanvas.width, annotationCanvas.height);

  console.log('📊 Canvas dimensions:', {
    canvasWidth: annotationCanvas.width,
    canvasHeight: annotationCanvas.height,
    videoWidth,
    videoHeight
  });

  // Extract zones by color
  const zones = extractZonesByColor(imageData);

  // Log what we found
  zones.forEach((zone, label) => {
    console.log(`🎨 Zone ${label}:`, zone.points.length, 'pixels');
  });

  // Convert zones to calibration config
  const holes = createHolesFromZones(zones, videoWidth, videoHeight);
  const startZone = createStartZoneFromZones(zones, videoWidth, videoHeight);
  const gutterZone = createGutterZoneFromZones(zones, videoWidth, videoHeight);

  console.log('🎯 Parsed calibration:', { holes, startZone, gutterZone });

  return {
    holes,
    startZone,
    gutterZone,
    timestamp: Date.now()
  };
}

/**
 * Extract colored regions from the annotation image
 */
function extractZonesByColor(imageData: ImageData): Map<string, ColorZone> {
  const zones = new Map<string, ColorZone>();
  
  // Initialize zones for each color
  Object.entries(WALL_COLORS).forEach(([label, color]) => {
    zones.set(label, { color, label, points: [] });
  });

  const data = imageData.data;
  const width = imageData.width;
  const height = imageData.height;

  // Scan image and collect points for each color
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = (y * width + x) * 4;
      const r = data[idx];
      const g = data[idx + 1];
      const b = data[idx + 2];
      const a = data[idx + 3];

      // Skip transparent pixels
      if (a < 128) continue;

      const pixelColor = rgbToHex(r, g, b);
      
      // Find matching zone
      for (const [label, zone] of zones.entries()) {
        if (colorsMatch(pixelColor, zone.color)) {
          zone.points.push({ x, y });
          break;
        }
      }
    }
  }

  return zones;
}

/**
 * Create holes from detected zones
 */
function createHolesFromZones(
  zones: Map<string, ColorZone>,
  videoWidth: number,
  videoHeight: number
): Hole[] {
  const holes: Hole[] = [];

  // Process circular holes (30pt, 40pt, 50pt, 100pt) - these are required
  const circularZones = ['30pt', '40pt', '50pt', '100pt'];

  circularZones.forEach(zoneLabel => {
    const zone = zones.get(zoneLabel);
    if (!zone || zone.points.length === 0) return;

    const circles = findCirclesInZone(zone);
    const value = parseInt(zoneLabel);

    circles.forEach((circle, index) => {
      console.log(`  ➡️ ${zoneLabel}-${index}:`, circle);
      holes.push({
        id: `${zoneLabel}-${index}`,
        x: circle.x,
        y: circle.y,
        radius: circle.radius,
        value,
        shape: 'circle'
      });
    });
  });

  // Find the 30pt and 40pt circles for calculating 20pt and 10pt zones
  const circle30 = holes.find(h => h.id === '30pt-0');
  const circle40 = holes.find(h => h.id === '40pt-0');

  // Calculate 20pt circle: centered at 30pt, large enough to contain 30pt and 40pt
  if (circle30 && circle40) {
    // Distance from 30pt center to 40pt center
    const dist30to40 = Math.sqrt(
      Math.pow(circle40.x - circle30.x, 2) +
      Math.pow(circle40.y - circle30.y, 2)
    );

    // Radius needs to reach from 30pt center to the far edge of 40pt circle
    const radius20 = dist30to40 + (circle40.radius || 0);

    console.log(`  ➡️ 20pt (calculated): center at 30pt, radius=${radius20.toFixed(1)}`);
    holes.push({
      id: '20pt',
      x: circle30.x,
      y: circle30.y,
      radius: radius20,
      value: 20,
      shape: 'circle'
    });
  } else {
    console.log('  ⚠️ 20pt: Cannot calculate (missing 30pt or 40pt)');
  }

  // Calculate 10pt circle: centered at 30pt, extends down to touch gutter top
  const gutterZone = zones.get('gutter');
  if (circle30 && gutterZone && gutterZone.points.length > 0) {
    // Find the highest point (minimum y) of the gutter zone
    const gutterTop = Math.min(...gutterZone.points.map(p => p.y));

    // Radius extends from 30pt center down to gutter top
    const radius10 = gutterTop - circle30.y;

    console.log(`  ➡️ 10pt (calculated): center at 30pt, radius=${radius10.toFixed(1)}, gutterTop=${gutterTop}`);
    holes.push({
      id: '10pt',
      x: circle30.x,
      y: circle30.y,
      radius: radius10,
      value: 10,
      shape: 'circle'
    });
  } else {
    console.log('  ⚠️ 10pt: Cannot calculate (missing 30pt or gutter)');
  }

  console.log('🔵 Total holes created:', holes.length);
  return holes;
}

/**
 * Calculate bounding box from a set of points
 */
function calculateBoundingBox(points: Array<{ x: number; y: number }>): { x: number; y: number; width: number; height: number } {
  if (points.length === 0) {
    return { x: 0, y: 0, width: 0, height: 0 };
  }

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  points.forEach(p => {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  });

  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY
  };
}

/**
 * Find circles in a zone by analyzing the drawn points
 */
function findCirclesInZone(zone: ColorZone): Array<{ x: number; y: number; radius: number }> {
  if (zone.points.length === 0) return [];

  // For 100pt, we expect 2 circles (left and right)
  if (zone.label === '100pt') {
    return findMultipleCircles(zone.points, 2);
  }

  // For other zones, find single circle
  const circle = findSingleCircle(zone.points);
  return circle ? [circle] : [];
}

/**
 * Find a single circle from points
 */
function findSingleCircle(points: Array<{ x: number; y: number }>): { x: number; y: number; radius: number } | null {
  if (points.length === 0) return null;

  // Calculate centroid
  const sumX = points.reduce((sum, p) => sum + p.x, 0);
  const sumY = points.reduce((sum, p) => sum + p.y, 0);
  const centerX = sumX / points.length;
  const centerY = sumY / points.length;

  // Calculate average radius
  const distances = points.map(p => 
    Math.sqrt((p.x - centerX) ** 2 + (p.y - centerY) ** 2)
  );
  const radius = distances.reduce((sum, d) => sum + d, 0) / distances.length;

  return { x: centerX, y: centerY, radius };
}

/**
 * Find multiple circles from points (for 100pt holes)
 */
function findMultipleCircles(
  points: Array<{ x: number; y: number }>,
  count: number
): Array<{ x: number; y: number; radius: number }> {
  if (points.length === 0) return [];

  // Use k-means clustering to separate the circles
  const clusters = kMeansClustering(points, count);

  return clusters.map(cluster => findSingleCircle(cluster)).filter(c => c !== null) as Array<{ x: number; y: number; radius: number }>;
}

/**
 * Simple k-means clustering
 */
function kMeansClustering(
  points: Array<{ x: number; y: number }>,
  k: number
): Array<Array<{ x: number; y: number }>> {
  if (points.length === 0 || k === 0) return [];
  if (k === 1) return [points];

  // Initialize centroids by spreading them across the x-axis
  const minX = Math.min(...points.map(p => p.x));
  const maxX = Math.max(...points.map(p => p.x));
  const avgY = points.reduce((sum, p) => sum + p.y, 0) / points.length;

  let centroids = Array.from({ length: k }, (_, i) => ({
    x: minX + (maxX - minX) * (i / (k - 1)),
    y: avgY
  }));

  // Run k-means for a few iterations
  for (let iter = 0; iter < 10; iter++) {
    // Assign points to nearest centroid
    const clusters: Array<Array<{ x: number; y: number }>> = Array.from({ length: k }, () => []);

    points.forEach(point => {
      let minDist = Infinity;
      let closestCluster = 0;

      centroids.forEach((centroid, i) => {
        const dist = Math.sqrt((point.x - centroid.x) ** 2 + (point.y - centroid.y) ** 2);
        if (dist < minDist) {
          minDist = dist;
          closestCluster = i;
        }
      });

      clusters[closestCluster].push(point);
    });

    // Update centroids
    centroids = clusters.map(cluster => {
      if (cluster.length === 0) return { x: 0, y: 0 };
      const sumX = cluster.reduce((sum, p) => sum + p.x, 0);
      const sumY = cluster.reduce((sum, p) => sum + p.y, 0);
      return { x: sumX / cluster.length, y: sumY / cluster.length };
    });
  }

  // Final assignment
  const finalClusters: Array<Array<{ x: number; y: number }>> = Array.from({ length: k }, () => []);
  points.forEach(point => {
    let minDist = Infinity;
    let closestCluster = 0;

    centroids.forEach((centroid, i) => {
      const dist = Math.sqrt((point.x - centroid.x) ** 2 + (point.y - centroid.y) ** 2);
      if (dist < minDist) {
        minDist = dist;
        closestCluster = i;
      }
    });

    finalClusters[closestCluster].push(point);
  });

  return finalClusters.filter(c => c.length > 0);
}

/**
 * Create start zone from detected zones
 */
function createStartZoneFromZones(
  zones: Map<string, ColorZone>,
  videoWidth: number,
  videoHeight: number
): Rectangle | undefined {
  const startZone = zones.get('start');
  if (!startZone || startZone.points.length === 0) return undefined;

  return createBoundingBox(startZone.points);
}

/**
 * Create gutter zone from detected zones
 */
function createGutterZoneFromZones(
  zones: Map<string, ColorZone>,
  videoWidth: number,
  videoHeight: number
): Rectangle | undefined {
  const gutterZone = zones.get('gutter');
  if (!gutterZone || gutterZone.points.length === 0) return undefined;

  return createBoundingBox(gutterZone.points);
}

/**
 * Create bounding box from points
 */
function createBoundingBox(points: Array<{ x: number; y: number }>): Rectangle {
  const minX = Math.min(...points.map(p => p.x));
  const maxX = Math.max(...points.map(p => p.x));
  const minY = Math.min(...points.map(p => p.y));
  const maxY = Math.max(...points.map(p => p.y));

  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY
  };
}

/**
 * Convert RGB to hex color
 */
function rgbToHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map(x => {
    const hex = x.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  }).join('');
}

/**
 * Check if two colors match (with tolerance)
 */
function colorsMatch(color1: string, color2: string, tolerance: number = 30): boolean {
  const rgb1 = hexToRgb(color1);
  const rgb2 = hexToRgb(color2);

  if (!rgb1 || !rgb2) return false;

  const rDiff = Math.abs(rgb1.r - rgb2.r);
  const gDiff = Math.abs(rgb1.g - rgb2.g);
  const bDiff = Math.abs(rgb1.b - rgb2.b);

  return rDiff <= tolerance && gDiff <= tolerance && bDiff <= tolerance;
}

/**
 * Convert hex to RGB
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
}

